import { ANONYMOUS_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  CommentState,
  ConversationMessageType,
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
  MessageVisibility,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getCustomerDisplayName } from "@redotech/redo-model/customer";
import { replaceReplyThreadClassesWithSameDefaultClass } from "@redotech/redo-model/email-utils/reply-thread-utils";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { AttachmentThumbnail } from "@redotech/redo-web/attachment-thumbnail";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import MeatballIcon from "@redotech/redo-web/icon-old/meatballs.svg";
import { ImageLightbox } from "@redotech/redo-web/image-lightbox";
import {
  pasteHtmlWithoutFocusing,
  QuillEditor,
} from "@redotech/redo-web/quill/quill-editor";
import * as quillEditorCss from "@redotech/redo-web/quill/quill-editor.module.css";
import { Spinner } from "@redotech/redo-web/spinner";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import Quill from "quill";
import {
  Fragment,
  memo,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getConversation, resendMessage } from "../client/conversations";
import { updateMetaComment } from "../client/facebook";
import { convertPlainTextToHtml } from "../conversation-email-view/format-email-message-content";
import * as messageCss from "../message.module.css";
import { SystemMessage } from "../system-message";
import { parseHtml, removeHtmlComments, removeSimpleHTMLTags } from "../utils";
import { parseTelnyxErrorMessage } from "../utils/telnyx-error-util";
import { MessageInformation } from "./message-information";
import { MessageActionsMenu } from "./meta-action-menu";

const urlRegex =
  /((?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$]))/gim;
const replaceLongLinks = (text: string) => {
  const pieces = text.split(urlRegex);
  const replacedPieces = pieces.map((piece, index) => {
    if (urlRegex.test(piece) && piece.length > 30) {
      // If the URL is part of an href, don't touch it
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (index > 0 && pieces[index - 1].endsWith('href="')) {
        return piece;
        // If the URL is the child of a tag like an <a> tag, shorten it
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      } else if (index > 0 && pieces[index - 1].endsWith('">')) {
        return `${piece.slice(0, 25)}...`;
        // If it's a bare URL, wrap it in an <a> tag
      } else {
        return `<a href="${piece}">${piece.slice(0, 25)}...</a>`;
      }
    } else {
      return piece;
    }
  });
  return replacedPieces.join("").replaceAll("\n", "<br/>");
};

const removeReplyThread = (
  htmlString: string,
  setShowViewReplyButton: {
    (value: SetStateAction<boolean>): void;
    (arg0: boolean): void;
  },
) => {
  htmlString = replaceReplyThreadClassesWithSameDefaultClass(htmlString);

  // Hide any reply threads in the email
  const htmlObject = document.createElement("div");
  htmlObject.innerHTML = htmlString;

  // Remove outlook reply threads
  // cspell:disable-next-line
  const firstThreadOutlook = htmlObject.querySelector("[id$='divRplyFwdMsg']");
  if (firstThreadOutlook) {
    let currentElement: Element | null = firstThreadOutlook;
    let nextElement: Element | null = firstThreadOutlook.nextElementSibling;
    while (currentElement) {
      currentElement.remove();
      currentElement = nextElement;
      nextElement = nextElement?.nextElementSibling || null;
    }
    setShowViewReplyButton(true);
  }

  const elementsToRemove = htmlObject.querySelectorAll(
    "[class$='gmail_quote']",
  );

  if (elementsToRemove.length > 0) {
    setShowViewReplyButton(true);
  }
  for (const element of elementsToRemove) {
    element.remove();
  }
  return htmlObject;
};

export const Message = memo(function Message({
  message,
  conversation,
  index,
  profilePictures,
  setPrivateReplyModalOpen,
  setPrivateReplyMessage,
  setActiveConversation,
}: {
  message: ExpandedConversationMessage;
  conversation: ExpandedConversation;
  index: number;
  profilePictures?: Record<string, string>;
  setPrivateReplyModalOpen: (value: boolean) => void;
  setPrivateReplyMessage: (value: ExpandedConversationMessage) => void;
  setActiveConversation: (conversation: MinimalConversation) => void;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);

  const [selectedImage, setSelectedImage] = useState<string>("");
  const [selectedVideo, setSelectedVideo] = useState<string>("");
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);

  const getCommentId = (): string | null => {
    if (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS) {
      return message.instagram?.comment?.commentId ?? null;
    } else if (
      conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS
    ) {
      return message.facebook?.comment?.commentId ?? null;
    }
    return null;
  };

  const handleUpdateComment = async (updatedText: string) => {
    try {
      setIsActionLoading(true);
      const commentId = getCommentId();

      if (!commentId) {
        toast("Comment ID not found.", { variant: "error" });
        return;
      }

      const platform =
        conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
          ? "instagram"
          : "facebook";

      const result = await updateMetaComment(client, {
        conversationId: conversation._id,
        commentId,
        message: updatedText,
        platform,
      });

      if (!result.success) {
        toast("Failed to update comment. Please try again.", {
          variant: "error",
        });
        return;
      }

      message.content = updatedText;

      const updatedConversation = await getConversation(client, {
        conversationId: conversation._id,
      });
      setActiveConversation(updatedConversation);

      setUpdateModalOpen(false);
    } catch (error) {
      console.error("Error updating comment:", error);
      toast("Failed to update comment. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const typeClassname =
    message.visibility === MessageVisibility.INTERNAL
      ? messageCss.internal
      : message.type === ConversationMessageType.MERCHANT
        ? messageCss.merchant
        : messageCss.customer;
  const senderClassname =
    message.type === ConversationMessageType.MERCHANT
      ? messageCss.merchant
      : messageCss.customer;

  const senderName: string | undefined =
    message.type === ConversationMessageType.MERCHANT
      ? message.user?._id === ANONYMOUS_USER_ID
        ? `${capitalize(conversation.platform)} User`
        : message.user?.name
      : ![
            ConversationPlatform.INSTAGRAM_COMMENTS,
            ConversationPlatform.FACEBOOK_COMMENTS,
          ].includes(conversation.platform) ||
          message.customer?._id === conversation.customer?.customer
        ? getCustomerDisplayName(message.customer)
        : undefined;

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setIsLightboxOpen(true);
  };

  const handleVideoClick = (videoUrl: string) => {
    setSelectedVideo(videoUrl);
    setIsLightboxOpen(true);
  };

  const handleResend = useHandler(async () => {
    const updatedConversation = await resendMessage(client, {
      conversationId: conversation._id,
      redoMessageId: message._id,
    });
    setActiveConversation(updatedConversation);
  });

  async function mediaIsVideo(url: string) {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.startsWith("video/")) {
        return true;
      }
    } catch (e) {
      // Ignore errors here and just say its not a video
    }
    return false;
  }

  const messageLoad = useLoad(async () => {
    const messageArray = removeSimpleHTMLTags(message.content).split(urlRegex);
    const images: string[] = [];
    const contentElements: string[] = [];
    const videoUrls: string[] = [];
    // Display an image instead of the url for image urls not in <img> tags
    for (const word of messageArray) {
      if (urlRegex.test(word)) {
        // Check if the url is preceded by "src", indicating it's already displaying inline
        if (
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          contentElements[contentElements.length - 1]
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            .slice(contentElements[contentElements.length - 1].length - 5)
            .includes("src=")
        ) {
          return;
        }
        if (
          word.match(/(https?:\/\/\S+(\.png|\.jpg|\.gif|\.jpeg|\.webp))/g) ||
          word.includes("ig_messaging_cdn")
        ) {
          const isVideo = await mediaIsVideo(word);
          if (isVideo) {
            videoUrls.push(word);
          } else {
            images.push(word);
          }
        } else {
          contentElements.push(`<a href="${word}" target="_blank">${word}</a>`);
        }
      } else {
        contentElements.push(word);
      }
    }

    return { images, contentElements, videoUrls };
  }, [message.content]);

  const images = useMemo(
    () => messageLoad.value?.images ?? [],
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageLoad.value?.images.length, conversation._id],
  );
  const contentElements = useMemo(
    () => messageLoad.value?.contentElements ?? [],
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageLoad.value?.contentElements.length, conversation._id],
  );
  const videoUrls = useMemo(
    () => messageLoad.value?.videoUrls ?? [],
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageLoad.value?.videoUrls.length, conversation._id],
  );

  return message.type !== "system" &&
    !messageLoad.pending &&
    messageLoad.value ? (
    <>
      {(contentElements.length > 0 ||
        images.length > 0 ||
        message.email?.htmlBody) && (
        <div className={classNames(messageCss.message, senderClassname)}>
          <div
            className={classNames(messageCss.bubbleContext, senderClassname)}
          >
            <MessageInformation
              conversation={conversation}
              isActionLoading={isActionLoading}
              message={message}
              senderName={senderName}
              setActionMenuAnchor={setActionMenuAnchor}
            />
            <MessageImages
              conversation={conversation}
              handleImageClick={handleImageClick}
              images={images}
              message={message}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              senderClassname={senderClassname}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              typeClassname={typeClassname}
            />
            <MessageVideos
              conversation={conversation}
              handleVideoClick={handleVideoClick}
              message={message}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              senderClassname={senderClassname}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              typeClassname={typeClassname}
              videoUrls={videoUrls}
            />
            {((contentElements.length > 0 &&
              !contentElements.every((elem) => !elem?.trim())) ||
              message.email?.htmlBody ||
              !!message.files?.length) && (
              <MessageBody
                contentElements={contentElements}
                conversation={conversation}
                index={index}
                message={message}
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                typeClassname={typeClassname}
              />
            )}
            <MessageFailure handleResend={handleResend} message={message} />
            <MessageActionsMenu
              actionMenuAnchor={actionMenuAnchor}
              conversation={conversation}
              message={message}
              setActionMenuAnchor={setActionMenuAnchor}
              setActiveConversation={setActiveConversation}
              setIsActionLoading={setIsActionLoading}
              setPrivateReplyMessage={setPrivateReplyMessage}
              setPrivateReplyModalOpen={setPrivateReplyModalOpen}
              setUpdateModalOpen={setUpdateModalOpen}
            />
          </div>
        </div>
      )}
      <ImageLightbox
        imageSrc={selectedImage}
        onClose={() => setIsLightboxOpen(false)}
        open={isLightboxOpen}
        videoSrc={selectedVideo}
      />

      <UpdateCommentModal
        initialText={message.content || ""}
        isOpen={updateModalOpen}
        isPending={isActionLoading}
        onClose={() => setUpdateModalOpen(false)}
        onUpdate={handleUpdateComment}
      />
    </>
  ) : (
    <SystemMessage message={message.content} />
  );
});

const MessageFailure = memo(function MessageFailure({
  message,
  handleResend,
}: {
  message: ExpandedConversationMessage;
  handleResend(): Promise<void>;
}) {
  // These error messages are not guaranteed to be customer friendly, and are
  // the product of unexpected errors. Don't expose them to the customer.
  const messageHadError = message.error;

  const [load, triggerLoad] = useTriggerLoad(handleResend);

  if (messageHadError) {
    const errorMessage = parseTelnyxErrorMessage(message.error);

    return (
      <RedoButton
        disabled={load.pending}
        hierarchy="secondary"
        onClick={() => triggerLoad()}
        pending={load.pending}
        size="xs"
        text={errorMessage}
        theme="destructive"
      />
    );
  } else {
    return null;
  }
});

const MessageVideos = memo(function MessageVideos({
  videoUrls,
  message,
  conversation,
  handleVideoClick,
  typeClassname,
  senderClassname,
}: {
  videoUrls: string[];
  message: ExpandedConversationMessage;
  conversation: ExpandedConversation;
  handleVideoClick: (videoUrl: string) => void;
  typeClassname: string;
  senderClassname: string;
}) {
  return (
    <>
      {videoUrls.map((videoUrl: string) => (
        <Fragment key={videoUrl}>
          {message.instagram?.mentionedStoryUrl && (
            <div>Mentioned you in their story</div>
          )}
          <div
            className={classNames(
              messageCss.bubble,
              typeClassname,
              conversation.platform,
            )}
          >
            <div
              className={classNames(messageCss.bubbleItems, senderClassname)}
              onClick={() => handleVideoClick(videoUrl)}
            >
              <video
                className={messageCss.uploadedImage}
                controls
                src={videoUrl}
              />
            </div>
          </div>
        </Fragment>
      ))}
    </>
  );
});

const ImageWithLoading = memo(function ImageWithLoading({
  src,
  className,
  onClick,
  onError,
}: {
  src: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    setHasError(true);
    onError?.(e);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "24px",
            height: "24px",
            zIndex: 1,
          }}
        >
          <Spinner />
        </div>
      )}
      <img
        className={className}
        onClick={onClick}
        onError={handleError}
        onLoad={handleLoad}
        src={src}
        style={{
          visibility: isLoading ? "hidden" : "visible",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease",
        }}
      />
    </div>
  );
});

const MessageImages = memo(function MessageImages({
  images,
  handleImageClick,
  message,
  conversation,
  typeClassname,
  senderClassname,
}: {
  images: string[];
  handleImageClick: (imageUrl: string) => void;
  message: ExpandedConversationMessage;
  conversation: ExpandedConversation;
  typeClassname: string;
  senderClassname: string;
}) {
  return (
    <>
      {images.map((image: string) => (
        <Fragment key={image}>
          {message.instagram?.mentionedStoryUrl && (
            <div>Mentioned you in their story</div>
          )}
          <div
            className={classNames(
              messageCss.bubble,
              typeClassname,
              conversation.platform,
            )}
          >
            <div
              className={classNames(messageCss.bubbleItems, senderClassname)}
            >
              <ImageWithLoading
                className={messageCss.uploadedImage}
                onClick={(e) => handleImageClick(e.currentTarget.src)}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.parentElement?.append(
                    document.createTextNode(
                      message.instagram?.mentionedStoryUrl
                        ? "Story expired"
                        : "Image unavailable",
                    ),
                  );
                }}
                src={image}
              />
            </div>
          </div>
        </Fragment>
      ))}
    </>
  );
});

const UpdateCommentModal = memo(function UpdateCommentModal({
  isOpen,
  onClose,
  initialText,
  onUpdate,
  isPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialText: string;
  onUpdate: (text: string) => Promise<void>;
  isPending: boolean;
}) {
  const [commentText, setCommentText] = useState(initialText);

  useEffect(() => {
    if (isOpen) {
      setCommentText(initialText);
    }
  }, [initialText, isOpen]);

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Update",
        onClick: async () => onUpdate(commentText),
        disabled: !commentText || commentText === initialText,
        pending: isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Update Comment"
    >
      <Flex dir="column" gap="md" p="md">
        <RedoTextInput
          placeholder="Enter your updated comment"
          setValue={setCommentText}
          value={commentText}
        />
      </Flex>
    </RedoModal>
  );
});

const MessageBody = memo(function MessageBody({
  typeClassname,
  conversation,
  contentElements,
  message,
  index,
}: {
  typeClassname: string;
  conversation: ExpandedConversation;
  contentElements: string[];
  message: ExpandedConversationMessage;
  index: number;
}) {
  const [quill, setQuill] = useState<Quill | null>(null);
  const [hideReplyThreads, setHideReplyThreads] = useState<boolean>(true);
  const [showViewReplyButton, setShowViewReplyButton] =
    useState<boolean>(false);

  useEffect(() => {
    if (quill) {
      if (
        message.facebook?.comment?.deleted ||
        message.facebook?.comment?.state === CommentState.DELETED ||
        message.instagram?.comment?.state === CommentState.DELETED
      ) {
        const htmlString = convertPlainTextToHtml(message.content);
        pasteHtmlWithoutFocusing(quill, htmlString);
      } else if (message.email?.htmlBody) {
        // Tables don't get good formatting with quill, so we will extract the table,
        // parse it to get the text content, then put it back in the message with some basic formatting
        const tableRegex = /<table.*\/table>/g;
        let htmlString = removeHtmlComments(message.email.htmlBody).replace(
          /(\r\n|\n|\r)/gm,
          "",
        );
        const tableMatches = htmlString.match(tableRegex);
        if (tableMatches) {
          for (const match of tableMatches) {
            htmlString = htmlString.replace(
              match,
              replaceLongLinks(parseHtml(match).replace("\n", "<br />")),
            );
          }
        }
        if (hideReplyThreads) {
          const htmlObject = removeReplyThread(
            htmlString,
            setShowViewReplyButton,
          );
          htmlString = htmlObject.outerHTML;
        }
        pasteHtmlWithoutFocusing(quill, htmlString);
      } else if (message.chatFlow?.htmlContent) {
        pasteHtmlWithoutFocusing(quill, message.chatFlow.htmlContent);
      } else if (contentElements.length > 0) {
        const htmlString = convertPlainTextToHtml(contentElements.join(""));
        pasteHtmlWithoutFocusing(quill, htmlString);
      } else {
        // Message.content should just be plaintext, with our own <a> tags added in to preserve links
        pasteHtmlWithoutFocusing(
          quill,
          convertPlainTextToHtml(message.content),
        );
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill, hideReplyThreads, contentElements.length, message]);

  return (
    <div
      className={classNames(
        messageCss.bubble,
        typeClassname,
        conversation.platform === "email" && messageCss.email,
        (message.instagram?.comment?.state === CommentState.HIDDEN ||
          message.instagram?.comment?.state === CommentState.DELETED) &&
          messageCss.hiddenComment,
        (message.facebook?.comment?.state === CommentState.HIDDEN ||
          message.facebook?.comment?.state === CommentState.DELETED ||
          message.facebook?.comment?.deleted) &&
          messageCss.hiddenComment,
      )}
    >
      {(contentElements.some((element) => element.length > 0) ||
        message.email?.htmlBody) && (
        <div
          className={classNames(quillEditorCss.quillContainerSmall)}
          data-testid={`message-bubble-${message._id}`}
        >
          <QuillEditor
            editorClassName={classNames(
              quillEditorCss.quillEditorSmall,
              quillEditorCss.removePadding,
            )}
            readOnly
            ref={setQuill}
            toolbarId={
              showViewReplyButton ? `messageToolbar${index}` : undefined
            }
          />
        </div>
      )}

      {!!message.files?.length && (
        <div className={messageCss.fileList}>
          {message.files?.map((file) => (
            <AttachmentThumbnail
              description={file.description}
              key={file._id}
              mimeType={file.mimeType}
              restrictHeight={false}
              url={file.url}
            />
          ))}
        </div>
      )}
      {showViewReplyButton && (
        <div className={messageCss.quillToolbarContainer}>
          <div
            className={quillEditorCss.quillToolbar}
            id={`messageToolbar${index}`}
          >
            <Button
              onClick={() => {
                setHideReplyThreads(!hideReplyThreads);
              }}
              theme={ButtonTheme.GHOST}
            >
              <div
                className={
                  message.visibility === MessageVisibility.PUBLIC &&
                  message.type === ConversationMessageType.MERCHANT
                    ? messageCss.lightActionButton
                    : messageCss.actionButton
                }
              >
                <MeatballIcon />
              </div>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
